import { ReactElement, useCallback, useEffect, useState } from 'react';
import {
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  Menu,
  MenuItem,
  Stack,
  styled,
  Typography
} from '@mui/material';
import { StyledParams } from 'types/common';
import MessageIcon from '@mui/icons-material/Message';
import { CallMenuIcon, HomeMenuIcon, MapMenuIcon, SettingMenuIcon } from 'assets/icon';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ROLE } from 'const/common';
import { StorageServices } from 'services/storage';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import { UserInfo } from 'services/user';
import { getUserInfo } from 'services/auth';
import { useUser } from 'contexts/user';
import { useNewMessage } from 'contexts/newMessage';

const MENU = [
  {
    label: 'Home',
    value: 0,
    icon: <HomeMenuIcon />,
    iconActive: <HomeMenuIcon color={'#fff'} />,
    link: '/'
  },
  {
    label: 'Inbox',
    value: 1,
    icon: <MessageIcon />,
    iconActive: <MessageIcon style={{ background: '#fff' }} />,
    link: '/chat-screen'
  },
  {
    label: 'Map',
    value: 2,
    icon: <MapMenuIcon />,
    iconActive: <MapMenuIcon color={'#fff'} />,
    link: '/map'
  },
  {
    label: 'Calls',
    value: 3,
    icon: <CallMenuIcon />,
    iconActive: <CallMenuIcon color={'#fff'} />,
    link: '/calls'
  },
  {
    label: 'Settings',
    value: 4,
    icon: <SettingMenuIcon />,
    iconActive: <SettingMenuIcon color={'#fff'} />,
    link: '/setting'
  }
];
const storage = new StorageServices();

/**
 *
 * RequireAuth Component check auth
 *
 * */
export function LayoutButtonNavigation({ children }: { children: ReactElement }) {
  const role = storage.getRole();
  const accessToken = storage.getAccessToken();
  const navigate = useNavigate();
  const [value, setValue] = useState<number | null>(null);
  const [openMenu, setOpenMenu] = useState<boolean>(false);
  const { setUser, user } = useUser();
  const location = useLocation();

  const { isNewMessage } = useNewMessage();
  useEffect(() => {
    MENU.map((item) => {
      if (location.pathname.includes(item.link)) {
        setValue(item.value);
      }
    });
  }, [location.pathname]);

  const handleLogout = useCallback(() => {
    storage.removeAccessToken();
    storage.removeRefreshToken();
    storage.removeRole();
    setUser(null);
    navigate('/admin/login');
  }, [storage]);

  useEffect(() => {
    if (role !== ROLE.USER) {
      navigate('/forbidden');
    }
  }, [role]);

  useEffect(() => {
    if (!accessToken) {
      setUser(null);
      return;
    }
    if (user?.id) return;
    (async () => {
      try {
        const { user }: { user: UserInfo } = await getUserInfo();
        setUser(user);
      } catch (error) {
        console.log(error);
      }
    })();
  }, [accessToken, setUser, user?.id]);
  const isMapPage = location.pathname.includes('/map') && !location.pathname.includes('/map/file');
  const isChatScreen = location.pathname.includes('/chat-screen');

  return (
    <>
      <Stack sx={{ display: { xs: 'block', md: 'none' }, width: '100%' }}>
        <Wrapper>
          <Content
            style={{ paddingBottom: isMapPage || isChatScreen ? 0 : '16px', height: '100%' }}
            id={'scrollableDiv'}>
            {children}
          </Content>
          <BottomMenu style={{ width: '100%' }}>
            <BottomNavigationStyled
              value={value}
              onChange={(_, newValue) => {
                setValue(newValue);
              }}
              showLabels>
              {MENU?.map((item, index) => (
                <BottomNavigationActionStyled
                  onClick={() => navigate(item.link)}
                  key={index}
                  label={item.label}
                  icon={
                    <Box sx={{ position: 'relative' }}>
                      {item.icon}
                      {item.label === 'Inbox' && isNewMessage && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -2,
                            right: -2,
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            backgroundColor: 'red'
                          }}
                        />
                      )}
                    </Box>
                  }
                />
              ))}
            </BottomNavigationStyled>
          </BottomMenu>
        </Wrapper>
      </Stack>
      <Box sx={{ display: { xs: 'none', md: 'flex' }, width: '100%' }}>
        <Stack minHeight={'100dvh'} flexDirection={'row'} width={'100%'} position={'relative'}>
          <WrapperNavbar minWidth={'240px'}>
            <img
              style={{ cursor: 'pointer' }}
              alt={'img'}
              width={143}
              src={'/images/logo.png'}
              onClick={() => navigate('/')}
            />
            <WrapperDesktop>
              {MENU.map((item, index) => (
                <NavLink to={item.link} key={index}>
                  <Stack className={'button-navbar'}>
                    <WrapperItemDesktop gap={'14px'}>
                      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        {item.icon}
                        {item.label === 'Inbox' && isNewMessage && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: -4,
                              right: -4,
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              backgroundColor: 'red'
                            }}
                          />
                        )}
                      </Box>
                      <TextStyledDesktop className={'text-menu'}>{item.label}</TextStyledDesktop>
                    </WrapperItemDesktop>
                  </Stack>
                </NavLink>
              ))}
            </WrapperDesktop>
          </WrapperNavbar>
          <Stack
            flex={5}
            minHeight={'100dvh'}
            sx={{ background: '#fff', height: '100%' }}
            width={'calc(100% - 240px)'}
            maxWidth={'calc(100% - 240px)'}
            alignItems={'start'}>
            <WrapperHeader>
              <Stack />
              <WrapperRightHeader>
                <WrapperAvatar>
                  <AccountCircleIcon sx={{ width: '44px', height: ' 44px' }} />
                </WrapperAvatar>
                <WrapperName>
                  <TextStyled>
                    {user?.firstName} {user?.lastName}
                  </TextStyled>
                  <TextRoleStyled>{storage.getRole()?.replace('_', ' ')}</TextRoleStyled>
                </WrapperName>
                <WrapperIcon onClick={() => setOpenMenu(true)}>
                  <img alt={'icon'} src={'/icons/chevron_down.svg'} />
                </WrapperIcon>
                <MenuStyled
                  open={openMenu}
                  onClose={() => setOpenMenu(false)}
                  MenuListProps={{
                    'aria-labelledby': 'basic-button'
                  }}>
                  <MenuItem style={{ gap: '10px' }} onClick={handleLogout}>
                    <LogoutIcon />
                    <Typography style={{ fontWeight: 500 }}>Logout</Typography>
                  </MenuItem>
                </MenuStyled>
              </WrapperRightHeader>
            </WrapperHeader>
            <Stack
              id={'scrollableDiv-web'}
              style={{
                height: 'calc(100dvh - 70px)',
                width: '100%',
                overflow: 'auto',
                maxWidth: '2000px'
              }}
              px={{ md: isMapPage ? 0 : 2, lg: isMapPage ? 0 : 4 }}
              pb={isMapPage ? 0 : 4}
              alignItems={'center'}>
              {children}
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </>
  );
}

const Wrapper = styled(Stack)(() => ({
  height: '100dvh',
  justifyContent: 'space-between',
  width: '100%',
  overflow: 'hidden'
}));

const Content = styled(Box)(() => ({
  flexGrow: 1,
  overflowY: 'auto',
  paddingBottom: '16px'
}));

const BottomMenu = styled(Box)(({ theme }: StyledParams) => ({
  background: theme.colors.dark1
}));
const BottomNavigationStyled = styled(BottomNavigation)(({ theme }: StyledParams) => ({
  background: theme.colors.dark1,
  justifyContent: 'space-around',
  alignItems: 'flex-start',
  height: 80
}));
const BottomNavigationActionStyled = styled(BottomNavigationAction)(({ theme }: StyledParams) => ({
  paddingTop: 0,
  height: '100%',
  minWidth: 60,
  gap: 4,
  color: theme.colors.light2,
  '&.Mui-selected': {
    color: theme.colors.white
  },
  '& .MuiBottomNavigationAction-label': {
    fontWeight: 400,
    '&.Mui-selected': {
      fontWeight: 700
    }
  }
}));

const WrapperDesktop = styled(Stack)(() => ({
  width: '100%',
  marginTop: '20px',
  '.active': {
    '.button-navbar': {
      background: `#22374A !important`,
      '.text-menu': {
        color: '#fff'
      },
      svg: {
        fill: '#fff',
        color: '#fff'
      }
    }
  }
}));

const WrapperNavbar = styled(Stack)(() => ({
  flex: 1,
  padding: '40px 0',
  maxWidth: 240,
  background: '#F4F5F6',
  alignItems: 'center',
  borderRight: '1px solid #BABAB6'
}));

const WrapperItemDesktop = styled(Stack)(() => ({
  alignItems: 'center',
  justifyContent: 'center',
  height: '50px',
  flexDirection: 'row',
  width: 'fit-content',
  marginLeft: '30px'
}));

const TextStyledDesktop = styled(Typography)(() => ({
  fontSize: '14px',
  fontWeight: '700',
  color: '#202224'
}));

const MenuStyled = styled(Menu)(() => ({
  '.MuiPaper-root': {
    minWidth: 130,
    top: '53px !important',
    right: '32px !important',
    bottom: 'unset !important',
    left: 'unset !important'
  }
}));
const TextRoleStyled = styled(Typography)(() => ({
  fontSize: '12px',
  fontWeight: '600',
  color: '#565656'
}));
const WrapperHeader = styled(Stack)(() => ({
  width: '100%',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '70px',
  background: '#F4F5F6',
  borderBottom: '1px solid #BABAB6',
  flexDirection: 'row',
  padding: '0 30px 0 40px'
}));
const WrapperRightHeader = styled(Stack)(() => ({
  gap: 20,
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'row'
}));
const WrapperAvatar = styled(Box)(() => ({
  height: 44,
  width: 44,
  borderRadius: 22
}));
const WrapperIcon = styled(Button)(() => ({
  height: 18,
  width: 18,
  borderRadius: 9,
  border: '0.2px solid #5C5C5C',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 0
}));
const WrapperName = styled(Stack)(() => ({
  gap: '4px'
}));
const TextStyled = styled(Typography)(() => ({
  fontSize: '14px',
  fontWeight: '700',
  color: '#202224'
}));
